import {
  Container,
  Card,
  Table,
  Badge,
  Button,
  Group,
  Stack,
  Title,
  Text,
  Loader,
  TextInput
} from "@mantine/core";

import axios from "axios";
import { useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import useFetch from "../../clients/features/get_withcredentials";

const Bills = () => {

  const { hotelId } = useParams();

  const { data = [], refreshData } = useFetch(
    `https://api.komorotel.com/api/reservation/hotel/${hotelId}`
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const normalize = (date) => {
    const d = new Date(date);
    d.setUTCHours(0, 0, 0, 0); // 🔥 IMPORTANT
    return d;
  };

  const getStatus = (status) => {
    switch (status) {
      case "confirmed":
        return { label: "Confirmée", color: "green" };
      case "pending":
        return { label: "En attente", color: "yellow" };
      case "cancelled":
        return { label: "Annulée", color: "red" };
      case "pending_cancel":
        return { label: "Annulation demandée", color: "orange" };
      case "no_show":
        return { label: "No-show", color: "dark" };
      default:
        return { label: "Inconnu", color: "gray" };
    }
  };

  useEffect(() => {

    const fetchcancellationRequests = async () => {

      try {

        const res = await axios.get(
          `https://api.komorotel.com/api/reservation/${hotelId}/pending-cancellations`,
          { withCredentials: true }
        );

        setCancellationRequests(res.data);

      } catch (err) {

        setError(err.message);

      } finally {

        setLoading(false);

      }

    };

    fetchcancellationRequests();

  }, [hotelId]);

  const calculateNights = (checkInDate, checkOutDate) => {

    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);

    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));

  };

  const toggleSort = (field) => {

    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }

  };

  const processedReservations = useMemo(() => {

   let filtered = data.filter((reservation) => {
      const text = search.toLowerCase();

      const matchSearch =
        reservation.guest?.firstName?.toLowerCase().includes(text) ||
        reservation.guest?.lastName?.toLowerCase().includes(text) ||
        reservation.roomId?.title?.toLowerCase().includes(text) ||
        reservation._id?.toLowerCase().includes(text);

      const matchStatus =
        statusFilter === "all" || reservation.status === statusFilter;

      return matchSearch && matchStatus;
    });

    filtered.sort((a, b) => {

      let valueA;
      let valueB;

      if (sortField === "guest") {
        valueA = a.guest?.lastName || "";
        valueB = b.guest?.lastName || "";
      }

      if (sortField === "createdAt") {
        valueA = new Date(a.createdAt);
        valueB = new Date(b.createdAt);
      }

      if (sortField === "price") {
        valueA = a.totalPrice;
        valueB = b.totalPrice;
      }

      if (sortField === "room") {
        valueA = a.roomId?.title || "";
        valueB = b.roomId?.title || "";
      }

      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;

      return 0;

    });

    return filtered;

  }, [data, search, sortField, sortDirection, statusFilter]);

  const stats = useMemo(() => {
    return {
      all: data.length,
      confirmed: data.filter(r => r.status === "confirmed").length,
      pending: data.filter(r => r.status === "pending").length,
      pending_cancel: data.filter(r => r.status === "pending_cancel").length,
      cancelled: data.filter(r => r.status === "cancelled").length,
      no_show: data.filter(r => r.status === "no_show").length,
    };
  }, [data]);

  const handleValidateReservation = async (reservationId) => {

    try {

      setLoading(true);

      await axios.put(
        `https://api.komorotel.com/api/reservation/validate-requests/${reservationId}`,
        {},
        { withCredentials: true }
      );

      alert("Réservation confirmée");

    } catch (err) {

      console.error(err);

    } finally {

      refreshData();
      setLoading(false);

    }

  };

  const handleNoShow = async (reservationId) => {
  try {
    setLoading(true);

    await axios.put(
      `https://api.komorotel.com/api/reservation/no-show/${reservationId}`,
      {},
      { withCredentials: true }
    );

    alert("Réservation marquée en no-show");

  } catch (err) {
    console.error(err);
  } finally {
    refreshData();
    setLoading(false);
  }
};

  const handleDeleteReservation = async (requestId) => {

    try {

      setLoading(true);

      await axios.put(
        `https://api.komorotel.com/api/reservation/cancellation-requests/${requestId}`,
        { status: "approved" },
        { withCredentials: true }
      );

      alert("Réservation annulée définitivement");

    } catch (err) {

      console.error(err);

    } finally {

      refreshData();
      setLoading(false);

    }

  };
  
  

  return (

    <Container size="lg">

      <Stack gap="lg">

        <div>

          <Title order={2}>Réservations</Title>

          <Text c="dimmed" size="sm">
            Gérez les réservations de votre hôtel
          </Text>

        </div>

        <TextInput
          placeholder="Rechercher client, chambre ou ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (

          <Group justify="center">
            <Loader />
          </Group>

        ) : error ? (

          <Text c="red">{error}</Text>

        ) : (

          <Card shadow="sm" radius="md" withBorder>
            <Group mb="xs" justify="space-between" align="center">

              <Stack gap={0}>
                <Text fw={600}>
                  {stats.all} réservations
                </Text>
                <Text size="xs" c="dimmed">
                  {statusFilter === "all"
                    ? "Toutes les réservations"
                    : `Filtre : ${getStatus(statusFilter).label}`}
                </Text>
              </Stack>

            </Group>
            <Group mb="md" wrap="wrap">

              <Button
                size="xs"
                variant={statusFilter === "all" ? "filled" : "light"}
                onClick={() => setStatusFilter("all")}
              >
                Toutes ({stats.all})
              </Button>

              <Button
                size="xs"
                color="green"
                variant={statusFilter === "confirmed" ? "filled" : "light"}
                onClick={() => setStatusFilter("confirmed")}
              >
                Confirmées ({stats.confirmed})
              </Button>

              <Button
                size="xs"
                color="yellow"
                variant={statusFilter === "pending" ? "filled" : "light"}
                onClick={() => setStatusFilter("pending")}
              >
                En attente ({stats.pending})
              </Button>

              <Button
                size="xs"
                color="orange"
                variant={statusFilter === "pending_cancel" ? "filled" : "light"}
                onClick={() => setStatusFilter("pending_cancel")}
              >
                Annulations ({stats.pending_cancel})
              </Button>

              <Button
                size="xs"
                color="red"
                variant={statusFilter === "cancelled" ? "filled" : "light"}
                onClick={() => setStatusFilter("cancelled")}
              >
                Annulées ({stats.cancelled})
              </Button>

              <Button
                size="xs"
                color="dark"
                variant={statusFilter === "no_show" ? "filled" : "light"}
                onClick={() => setStatusFilter("no_show")}
              >
                No-show ({stats.no_show})
              </Button>

            </Group>

            <Table highlightOnHover>

              <thead>

                <tr>

                  <th onClick={() => toggleSort("id")}>ID</th>
                  <th onClick={() => toggleSort("guest")}>Client</th>
                  <th onClick={() => toggleSort("createdAt")}>Date</th>
                  <th>Nuits</th>
                  <th onClick={() => toggleSort("room")}>Chambre</th>
                  <th onClick={() => toggleSort("price")}>Prix</th>
                  <th>Status</th>
                  <th>Actions</th>
                  <th>Annulation</th>

                </tr>

              </thead>

              <tbody>

                {processedReservations.map((reservation) => {
                  const today = normalize(new Date())

                  const checkIn = normalize(reservation.checkInDate);
                  const checkOut = normalize(reservation.checkOutDate);

      

                  const isUpcoming = today < checkIn;
                  const isOngoing = today >= checkIn && today < checkOut;
                  const isPast = today >= checkOut;

                  const cancellationRequest = cancellationRequests?.find(
                    r => String(r.reservationId?._id || r.reservationId) === String(reservation._id)
                  );
                  const displayId =
                    reservation?.reservationId ||
                    reservation._id.slice(0,6).toUpperCase();
                    const status = getStatus(reservation.status);



                  return (

                    <tr
                      key={reservation._id}
                      style={{
                        backgroundColor:
                          reservation.status === "cancelled"
                            ? "#fff5f5"
                            : reservation.status === "pending_cancel"
                            ? "#fffbe6"
                            : reservation.status === "no_show"
                            ? "#f1f3f5"
                            : "white"
                      }}
                    >

                      <td>{displayId}</td>

                      <td>
                        {reservation.guest?.firstName} {reservation.guest?.lastName}
                      </td>

                      <td>
                        <Stack gap={2}>
                          <Text size="sm">
                            {new Date(reservation.checkInDate).toLocaleDateString("fr-FR", {
                              day: "numeric",
                              month: "short"
                            })} →{" "}
                            {new Date(reservation.checkOutDate).toLocaleDateString("fr-FR", {
                              day: "numeric",
                              month: "short"
                            })}
                          </Text>

                        
                        </Stack>
                      </td>

                      <td>
                        {calculateNights(
                          reservation.checkInDate,
                          reservation.checkOutDate
                        )}
                      </td>

                      <td>{reservation.roomId?.title}</td>

                      <td>
                        <strong>{reservation.totalPrice} €</strong>
                      </td>

                      <td>
                        <Stack gap={4}>
                          <Badge color={status.color}>
                            {status.label}
                          </Badge>

                          {reservation.status === "confirmed" && isUpcoming && (
                            <Text size="xs" c="blue">À venir</Text>
                          )}

                          {reservation.status === "confirmed" && isOngoing && (
                            <Text size="xs" c="green">Séjour en cours</Text>
                          )}

                          {reservation.status === "confirmed" && isPast && (
                            <Text size="xs" c="dimmed">Séjour terminé</Text>
                          )}

                          {reservation.status === "no_show" && (
                            <Text size="xs" c="dark">Client absent</Text>
                          )}

                          {reservation.status === "pending_cancel" && (
                            <Text size="xs" c="orange">Demande d’annulation</Text>
                          )}
                        </Stack>
                      </td>


                      <td>
                        <Group gap="xs">

                          {/* CONFIRMATION */}
                          {reservation.status === "pending" && (
                            <Button
                              size="xs"
                              color="green"
                              onClick={() => handleValidateReservation(reservation._id)}
                            >
                              Confirmer
                            </Button>
                          )}

                          {/* NO SHOW */}
                          {reservation.status === "confirmed" && isPast && (
                            <Button
                              size="xs"
                              color="dark"
                              variant="light"
                              onClick={() => handleNoShow(reservation._id)}
                            >
                              No-show
                            </Button>
                          )}

                        </Group>
                      </td>

                      <td>
                        {reservation.status === "pending_cancel" && cancellationRequest ? (

                          <Button
                            size="xs"
                            color="red"
                            onClick={() =>
                              handleDeleteReservation(cancellationRequest._id)
                            }
                          >
                            Confirmer annulation
                          </Button>

                        ) : reservation.status === "cancelled" ? (

                          <Text size="xs" c="red">
                            ✔ Annulée
                          </Text>

                        ) : (

                          <Text size="xs" c="dimmed">
                            —
                          </Text>

                        )}
                      </td>

                    </tr>

                  );

                })}

              </tbody>

            </Table>

          </Card>

        )}

      </Stack>

    </Container>

  );

};

export default Bills;